import { useCallback, useEffect, useMemo } from 'react';

import useSWRInfinite from 'swr/infinite';

import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';

import { ALLOY_KEY_PREFIX, baseParser, setDataInStorage } from '../commonCache';

const alloyPrefix = (str: string) => (str ? `${ALLOY_KEY_PREFIX}${str}` : str);

const removeSWRPart = (key: string) => {
  return key.replace('$inf$', '');
};

type Pagination = {
  limit: number;
  offset: number;
  totalResults: number;
  nextUrl?: string;
  previousUrl?: string;
};

const defaultGetNextKey = (data?: unknown) =>
  (data as { pagination: Pagination })?.pagination.nextUrl || '';

const loadDataFromSessionStorage = (
  url: string,
  getNextKey: (data: unknown) => string,
) => {
  const res = [];
  let currentUrl = url;
  while (currentUrl) {
    const data = baseParser(sessionStorage.getItem(alloyPrefix(currentUrl)));
    if (data) {
      res.push(data);
    }
    currentUrl = data && getNextKey(data);
  }
  return res.length === 0 ? undefined : res;
};

const saveInStorage = <T>(
  data: T[],
  key: string,
  getNextKey: (data: T) => string,
) => {
  const storeKey =
    data.length - 1 === 0
      ? removeSWRPart(key)
      : getNextKey(data[data.length - 2]);
  setDataInStorage(data[data.length - 1], storeKey);
};

const clearAllDataFromSessionStorage = <T>(
  url: string,
  getNextKey: (data: T) => string,
) => {
  const keysToRemove = [];
  let curKey = url;
  while (curKey !== '') {
    if (curKey) {
      keysToRemove.push(alloyPrefix(curKey));
    }
    curKey = getNextKey(
      JSON.parse(sessionStorage.getItem(alloyPrefix(curKey)) as string),
    );
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};

export const useGetAllPaginatedData = <T>({
  url,
  fetcher = fetchWithTokenAndRegion,
  getNextKey = defaultGetNextKey,
}: {
  url: string;
  fetcher?: (url: RequestInfo, fetchData?: RequestInit) => Promise<T>;
  getNextKey?: (data: T) => string;
}) => {
  const fallbackData = useMemo(
    () =>
      loadDataFromSessionStorage(url, getNextKey as (data: unknown) => string),
    [url, getNextKey],
  );

  const { data, error, mutate, setSize, isValidating } = useSWRInfinite<T>(
    (pageIndex, previousPageData) =>
      pageIndex === 0 ? url : getNextKey(previousPageData as T),
    fetcher,
    {
      fallbackData,
      onSuccess: (newData, key) => saveInStorage<T>(newData, key, getNextKey),
      revalidateOnMount: true,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      revalidateFirstPage: false,
    },
  );

  useEffect(() => {
    if (data && !!getNextKey(data[data.length - 1])) {
      setSize(data.length + 1);
    }
  }, [data, getNextKey, setSize]);

  const hasMoreData = useMemo(
    () => !!url && !(data && data.length && !getNextKey(data[data.length - 1])),
    [url, data, getNextKey],
  );

  const clearSessionStorage = useCallback(() => {
    clearAllDataFromSessionStorage(url, getNextKey);
  }, [getNextKey, url]);

  const loading: boolean = isValidating || (!error && hasMoreData);
  const retry = useCallback(() => mutate(), [mutate]);

  return {
    loading,
    retry,
    clearSessionStorage,
    data: data === undefined ? [] : data,
    error,
  };
};
